import { Injectable } from "@angular/core";
import { CompaniesGroupsService } from "../services/companies-groups.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as CompaniesGroupsActions from "./companies-groups.actions";
import { catchError, finalize, map, of, switchMap, tap } from "rxjs";
import { AppState } from "src/app/core/app.state";
import { Store } from "@ngrx/store";
import { SetLoading } from "src/app/shared/store/shared.actions";
import { APIResponse } from "src/app/core/models/api-response";
import { ICompaniesGroups } from "../models/companies-groups.model";
import { CommonService } from "src/app/shared/common.service";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_PAGE_SIZE } from "src/app/config/global.config";

@Injectable()
export class CompaniesGroupsEffects {
    constructor(
        private companiesGroupsService: CompaniesGroupsService,
        private actions$: Actions,
        private store: Store<AppState>,
        private commonService: CommonService,
        private translateService: TranslateService
    ) { }

    listCompaniesGroupsEffects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.FetchCompaniesGroups),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap(({ includes }) =>
                this.companiesGroupsService.listCompaniesGroups(includes).pipe(
                    map((res: APIResponse<ICompaniesGroups>) => CompaniesGroupsActions.FetchCompaniesGroupsSuccess({ allCompaniesGroups: res })),
                    catchError((err) => of(CompaniesGroupsActions.FetchCompaniesGroupsError({ error: err }))),
                    finalize(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false })))
                )
            )
        )
    );

    companiesGroupsEffects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.FetchAllCompaniesGroups),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap((action) =>
                this.companiesGroupsService.companiesGroups(action.page, action.filter, action.key).pipe(
                    map((companiesGroups: APIResponse<ICompaniesGroups>) => CompaniesGroupsActions.FetchAllCompaniesGroupsSuccess({ companiesGroups })),
                    catchError((err) => of(CompaniesGroupsActions.FetchAllCompaniesGroupsError({ error: err }))),
                    finalize(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false }))),
                )
            )
        )
    );

    AddCompaniesGroupsEffects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.AddCompaniesGroupe),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap((action) =>
                this.companiesGroupsService.addCompaniesGroups(action.payload).pipe(
                    map((payload: APIResponse<ICompaniesGroups>) => CompaniesGroupsActions.AddCompaniesGroupeSuccess({ companiesGroup: payload.object! })),
                    tap(() => {
                        this.commonService.showToast(
                        this.translateService.instant(`alerts.${AlertEnums.groupAddSuccess}`),
                        'SUCCESS'
                    );
                    }),
                    catchError((err) => of(CompaniesGroupsActions.AddCompaniesGroupeError({ error: err }))),
                    finalize(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false })))
                )
            )
        )
    );

    GetCompaniesGroupsByIdEffects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.GetCompaniesGroupeById),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap((action) =>
                this.companiesGroupsService.getCompaniesGroupsById(action.id).pipe(
                    map((payload: APIResponse<ICompaniesGroups>) => CompaniesGroupsActions.GetCompaniesGroupeByIdSuccess({ companiesGroup: payload.object! })),
                    catchError(() => of(CompaniesGroupsActions.GetCompaniesGroupeByIdError())),
                    finalize(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false })))
                )
            )
        )
    );

    // update groups
    UpdateCompaniesGroupsEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.EditCompaniesGroupe),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap((action) =>
                this.companiesGroupsService.editCompaniesGroups(action.id, action.payload).pipe(
                    map((res: APIResponse<ICompaniesGroups>) => CompaniesGroupsActions.EditCompaniesGroupeSuccess({ companiesGroup: res.data[0] })),
                    tap(() => {
                        this.commonService.showToast(
                            this.translateService.instant(`alerts.${AlertEnums.groupUpdateSuccess}`),
                            'SUCCESS'
                        );
                    }),
                    catchError(() => of(CompaniesGroupsActions.EditCompaniesGroupeError())),
                    finalize(() => {
                        this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false }));
                    })
                )
            )
        )
    );

    // delete CompaniesGroupe
    DeleteCompaniesGroupeEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompaniesGroupsActions.DeleteCompaniesGroups),
            tap(() => this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: true }))),
            switchMap((action) =>
                this.companiesGroupsService.deleteCompaniesGroups(action.id).pipe(
                    map(() => CompaniesGroupsActions.DeleteCompaniesGroupsSuccess()),
                    tap(() => {
                        this.commonService.showToast(
                            this.translateService.instant(`alerts.${AlertEnums.groupDeleteSuccess}`),
                            'SUCCESS'
                        );
                    }),
                    catchError(() => of(CompaniesGroupsActions.DeleteCompaniesGroupsError())),
                    finalize(() => {
                        this.store.dispatch(CompaniesGroupsActions.SetGroupsLoader({ isLoading: false }));
                    })
                )
            )
        )
    );
}
